import { NarrativePanel } from "./Narrative";
// Removed 'Panel' import
import { Table, ZoomIn, Megaphone, FlaskConical, ClipboardList } from "lucide-react";

// ====================================================================================
// HELPER: GLASS CONTAINER (Unified Aesthetic)
// ====================================================================================
const GlassContainer = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col ${className}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
            {Icon && <Icon size={16} className="text-indigo-400" />}
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">{title}</h3>
        </div>
        {/* Content */}
        <div className="p-4 flex-grow">
            {children}
        </div>
    </div>
);

// Updated signature to accept setDrawerData and priorityArchetypes
export const renderMessaging = ({ currentData, narrativeData, channelPriorities, setDrawerData, priorityArchetypes }) => (
        <div className="space-y-8 animate-in fade-in duration-500">
             {/* 1. Narrative Panel (Full Width - Already Glassmorphic) */}
            <NarrativePanel narrativeData={narrativeData} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 
                 {/* 2. Messaging Engine (Language & Persuasion) */}
                 <GlassContainer title="Messaging Engine" icon={FlaskConical}>
                     <div className="space-y-6">
                        {/* Language Guide */}
                        <div className="bg-slate-950/30 border border-slate-800/50 rounded-lg p-4">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Language Guide</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h5 className="font-semibold text-success-400 mb-2 text-xs uppercase tracking-wide">What Resonates</h5>
                                    <ul className="space-y-2 list-disc list-inside text-xs text-slate-300">
                                    {(currentData?.voter_targeting?.messaging_priorities?.what_resonates || []).length > 0 ? 
                                        currentData.voter_targeting.messaging_priorities.what_resonates.map((msg, i) => (
                                        <li key={i}>{msg}</li>
                                        )) : (
                                        <li className="list-none text-slate-500 italic">N/A</li>
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-danger-400 mb-2 text-xs uppercase tracking-wide">What to Avoid</h5>
                                    <ul className="space-y-2 list-disc list-inside text-xs text-slate-300">
                                    {(currentData?.voter_targeting?.messaging_priorities?.what_to_avoid || []).length > 0 ?
                                        currentData.voter_targeting.messaging_priorities.what_to_avoid.map((msg, i) => (
                                        <li key={i}>{msg}</li>
                                        )) : (
                                        <li className="list-none text-slate-500 italic">N/A</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Persuasion Strategy */}
                        <div className="bg-slate-950/30 border border-slate-800/50 rounded-lg p-4">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Persuasion & Activation Strategy</h4>
                            <div className="space-y-4">
                            <div>
                                <div className="text-[10px] text-accent-400 font-bold uppercase mb-1">Target Profile</div>
                                <div className="text-base font-semibold text-white tracking-tight">
                                {currentData?.voter_targeting?.persuasion_pathway_analysis?.target_profile || 'N/A'}
                                </div>
                                <p className="text-sm text-slate-300 leading-relaxed mt-1">
                                {currentData?.voter_targeting?.persuasion_pathway_analysis?.recommended_persuasion_style || 'N/A'}
                                </p>
                            </div>
                            <div className="pt-3 border-t border-slate-800/50">
                                <div className="text-[10px] text-accent-400 font-bold uppercase mb-1">GOTV Recommendation</div>
                                <p className="text-sm text-slate-300 leading-relaxed">
                                {currentData?.voter_targeting?.activation_vector_analysis?.gotv_recommendation || 'N/A'}
                                </p>
                            </div>
                            </div>
                        </div>
                     </div>
                 </GlassContainer>

                 {/* 3. Tactical & Resource Briefing */}
                 <GlassContainer title="Tactical & Resource Briefing" icon={ClipboardList}>
                    {/* Matrix Trigger Card */}
                    <div 
                        onClick={() => {
                            if (setDrawerData) {
                                setDrawerData({ 
                                    isOpen: true, 
                                    type: 'channelMatrix', 
                                    data: { 
                                        archetypes: priorityArchetypes || [], 
                                        wardChannels: channelPriorities,
                                        wardOverview: currentData 
                                    } 
                                });
                            } else {
                                console.error("setDrawerData is undefined. Ensure it is passed to renderMessaging.");
                            }
                        }}
                        className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 rounded-lg p-4 hover:border-accent-500/50 transition-all duration-200 cursor-pointer group mb-6 border border-slate-700/50 hover:shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                    >
                        <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-slate-900 rounded-md border border-slate-700 group-hover:border-accent-500/30 transition-colors">
                                <Table size={18} className="text-accent-400" />
                            </div>
                            <h4 className="font-semibold text-slate-200 group-hover:text-white transition-colors">Channel Efficiency Matrix</h4>
                        </div>
                        <ZoomIn size={16} className="text-slate-600 group-hover:text-accent-400 transition-colors" />
                        </div>
                        <p className="text-xs text-slate-400 mt-2 group-hover:text-slate-300 transition-colors">Click to open a detailed matrix comparing ward priorities with archetype receptivity.</p>
                    </div>

                    <div className="space-y-6">
                        {/* Channel Priorities */}
                        <div className="bg-slate-950/30 border border-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-4">
                                <Megaphone size={14} className="text-slate-400" />
                                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Ward Channel Priorities</h4>
                            </div>
                            <div className="space-y-3">
                                {channelPriorities.length > 0 ? channelPriorities.map(([channel, score]) => (
                                <div key={channel} className="text-sm">
                                    <div className="flex justify-between items-center mb-1.5">
                                    <span className="capitalize text-slate-300 font-medium text-xs">{channel.replace(/_/g, ' ')}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{(score * 100).toFixed(0)}</span>
                                    </div>
                                    <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                        <div className="bg-accent-500 h-1.5 rounded-full opacity-80" style={{ width: `${score * 100}%` }}></div>
                                    </div>
                                </div>
                                )) : (
                                <p className="text-sm text-slate-500 italic">No channel data available.</p>
                                )}
                            </div>
                        </div>
                        
                        {/* Style Guide */}
                        <div className="bg-slate-950/30 border border-slate-800/50 rounded-lg p-4">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Communication Style Guide</h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center pb-2 border-b border-slate-800/50 last:border-0 last:pb-0">
                                    <span className="text-slate-400 text-xs">Tone</span>
                                    <span className="text-white font-medium text-xs text-right">{currentData?.recommendations?.resources?.communication_style_guide?.tone || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-slate-800/50 last:border-0 last:pb-0">
                                    <span className="text-slate-400 text-xs">Language</span>
                                    <span className="text-white font-medium text-xs text-right">{currentData?.recommendations?.resources?.communication_style_guide?.language || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-400 text-xs">Format</span>
                                    <span className="text-white font-medium text-xs text-right">{currentData?.recommendations?.resources?.communication_style_guide?.format || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Resource Focus */}
                        <div className="bg-slate-950/30 border border-slate-800/50 rounded-lg p-4">
                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Resource Focus</h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center pb-2 border-b border-slate-800/50">
                                    <span className="text-slate-400 text-xs">Budget Range</span>
                                    <span className="text-emerald-400 font-mono font-bold text-xs">{currentData?.recommendations?.resources?.budget_allocation_pct_range || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-start">
                                    <span className="text-slate-400 text-xs mt-0.5">Volunteer Focus</span>
                                    <span className="text-white font-medium text-xs text-right max-w-[60%] leading-tight">{currentData?.recommendations?.resources?.volunteer_focus || 'N/A'}</span>
                                </div> 
                            </div>
                        </div>
                    </div>
                 </GlassContainer>
            </div>
        </div>
    );